import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Check, Clock, X } from "lucide-react";
import {
  getDefaultLateTime,
  calculateLateMinutes,
  getLessonEndTime,
  clampArrivalTime,
} from "../utils/attendanceHelpers";
import type { TempAttendanceMap } from "../types/attendanceDialog";
import { DistributionSourceBadge } from "../../class-distribution";
import type { DistributionSourceLabel } from "../../class-distribution";

// ============================================================================
// Types
// ============================================================================

interface EditStudentRowProps {
  student: {
    id: string;
    name: string;
    className?: string;
    isGuest?: boolean;
    guestSourceClassName?: string | null;
    guestSourceLabel?: DistributionSourceLabel | null;
  };
  attendance: TempAttendanceMap[string] | undefined;
  isMobile: boolean;
  isKb: boolean;
  isCourse: boolean;
  isPending: boolean;
  pendingRed: boolean;
  selectedLesson: any;
  setStudentStatus: (
    studentId: string,
    status: "present" | "late" | "excused" | "unexcused",
  ) => void;
  setTempAttendance: React.Dispatch<React.SetStateAction<TempAttendanceMap>>;
  formatStudentName: (name: string) => string;
  /** When set, shows a × button next to the Gast badge that opens the
   *  remove-Tagesgast confirmation. Only meaningful for guest students in
   *  course lessons. */
  onRemoveGuest?: () => void;
  /** True when an excusing absence note (krank / beurlaubt / informiert)
   *  already covers this lesson's date. The "U" (Ungeklärt) action is
   *  then disabled — the absence is already explained, so re-flagging it
   *  as unklar would spawn a bogus Klärung for the secretariat. */
  hasCoveringAbsenceNote?: boolean;
}

// ============================================================================
// Shared sub-sections
// ============================================================================

/** Student name + KB tag + class name badge */
function StudentName({
  student,
  isKb,
  isCourse,
  nameClass,
  formatStudentName,
  onRemoveGuest,
}: {
  student: EditStudentRowProps["student"];
  isKb: boolean;
  isCourse: boolean;
  nameClass: string;
  formatStudentName: (name: string) => string;
  onRemoveGuest?: () => void;
}) {
  return (
    <div className={`text-sm flex items-center gap-2 min-w-0 ${nameClass}`}>
      {formatStudentName(student.name)}
      {isKb && (
        <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700">
          KB
        </span>
      )}
      {student.isGuest && (
        student.guestSourceClassName || student.guestSourceLabel ? (
          // Class-distribution guest: show source badge ("von 3A · Ausfall")
          <DistributionSourceBadge
            sourceClassName={student.guestSourceClassName}
            sourceLabel={student.guestSourceLabel}
          />
        ) : (
          // Course Tagesgast: simple badge with optional remove button
          <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
            Gast
            {onRemoveGuest && (
              <button
                type="button"
                onClick={onRemoveGuest}
                aria-label={`${formatStudentName(student.name)} als Tagesgast entfernen`}
                title="Tagesgast entfernen"
                className="ml-1 inline-flex items-center justify-center rounded-full hover:bg-blue-200 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        )
      )}
      {isCourse && student.className && (
        <span className="text-xs text-gray-400 font-normal">
          ({student.className})
        </span>
      )}
    </div>
  );
}

/** Excuse reason textarea (shown when status === "excused") */
function ExcuseField({
  studentId,
  excuseReason,
  setTempAttendance,
  wrapperClass,
}: {
  studentId: string;
  excuseReason: string;
  setTempAttendance: EditStudentRowProps["setTempAttendance"];
  wrapperClass: string;
}) {
  return (
    <div className={wrapperClass}>
      <Textarea
        placeholder="z.B. Krankheit, Arzttermin..."
        value={excuseReason}
        onChange={(e) =>
          setTempAttendance((prev) => ({
            ...prev,
            [studentId]: {
              ...prev[studentId],
              excuseReason: e.target.value,
            },
          }))
        }
        rows={1}
        className="min-h-0 py-1.5 text-sm resize-none"
        style={{ fieldSizing: "content" } as React.CSSProperties}
      />
    </div>
  );
}

/** Late time picker + checkbox (shown when status === "late") */
function LateFields({
  studentId,
  attendance,
  selectedLesson,
  setTempAttendance,
  wrapperClass,
}: {
  studentId: string;
  attendance: NonNullable<TempAttendanceMap[string]>;
  selectedLesson: any;
  setTempAttendance: EditStudentRowProps["setTempAttendance"];
  wrapperClass: string;
}) {
  return (
    <div className={`${wrapperClass} space-y-2`}>
      <div className="flex items-center gap-2">
        <Label
          htmlFor={`arrival-${studentId}`}
          className="text-xs text-gray-600"
        >
          Ankunft
        </Label>
        <Input
          id={`arrival-${studentId}`}
          type="time"
          min={selectedLesson?.time}
          max={getLessonEndTime(selectedLesson)}
          value={attendance.arrivalTime || ""}
          onChange={(e) => {
            const clampedArrivalTime = clampArrivalTime(
              e.target.value,
              selectedLesson,
            );
            const newMinutesLate = calculateLateMinutes(
              clampedArrivalTime,
              selectedLesson,
            );
            setTempAttendance((prev) => ({
              ...prev,
              [studentId]: {
                ...prev[studentId],
                arrivalTime: clampedArrivalTime,
                minutesLate: newMinutesLate,
              },
            }));
          }}
          className="h-8"
        />
        <div className="text-xs text-gray-500">
          {attendance.minutesLate || 5} Minuten verspätet
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`late-excused-${studentId}`}
          checked={attendance.lateExcused || false}
          onCheckedChange={(checked) =>
            setTempAttendance((prev) => ({
              ...prev,
              [studentId]: {
                ...prev[studentId],
                lateExcused: !!checked,
              },
            }))
          }
        />
        <Label
          htmlFor={`late-excused-${studentId}`}
          className="text-xs text-gray-600"
        >
          Verspätung entschuldigt
        </Label>
      </div>
    </div>
  );
}

// ============================================================================
// Mobile Row
// ============================================================================

function MobileStudentRow({
  student,
  attendance,
  isKb,
  isCourse,
  nameClass,
  selectedLesson,
  setStudentStatus,
  setTempAttendance,
  formatStudentName,
  onRemoveGuest,
  hasCoveringAbsenceNote,
}: {
  student: EditStudentRowProps["student"];
  attendance: EditStudentRowProps["attendance"];
  isKb: boolean;
  isCourse: boolean;
  nameClass: string;
  selectedLesson: any;
  setStudentStatus: EditStudentRowProps["setStudentStatus"];
  setTempAttendance: EditStudentRowProps["setTempAttendance"];
  formatStudentName: (name: string) => string;
  onRemoveGuest?: () => void;
  hasCoveringAbsenceNote: boolean;
}) {
  const uDisabled = isKb || hasCoveringAbsenceNote;
  const sl = selectedLesson as {
    is_course?: boolean;
    parallel_main_lesson_id?: string | null;
  };
  const courseAbsentGuardActive =
    !!sl?.is_course && !!sl?.parallel_main_lesson_id;

  return (
    <div className="space-y-1 py-1 px-1 hover:bg-gray-50 rounded">
      <div
        className="grid gap-1 items-center"
        style={{ gridTemplateColumns: "3fr repeat(4, 1fr)" }}
      >
        <StudentName
          student={student}
          isKb={isKb}
          isCourse={isCourse}
          nameClass={nameClass}
          formatStudentName={formatStudentName}
          onRemoveGuest={onRemoveGuest}
        />
        {/* Anw / Spät — one cycle button: first tap = check (Anwesend),
            second tap = clock (Verspätet), third tap clears. Spans two
            grid columns so it sits centred under the check-Alle and
            clock-Alle header buttons above. */}
        <div
          className="flex justify-center"
          style={{ gridColumn: "span 2" }}
        >
          <Button
            variant="ghost"
            size="sm"
            aria-label={
              attendance?.status === "late" ? "Verspätet" : "Anwesend"
            }
            className={`h-8 w-8 p-0 ${
              attendance?.status === "present"
                ? "text-green-600 bg-green-50 hover:bg-green-100"
                : attendance?.status === "late"
                ? "text-yellow-600 bg-yellow-50 hover:bg-yellow-100"
                : "text-gray-300 hover:text-green-600 hover:bg-green-50"
            }`}
            onClick={() =>
              setTempAttendance((prev) => {
                const current = prev[student.id];
                if (current?.status === "present") {
                  const arrivalTime =
                    current?.arrivalTime || getDefaultLateTime(selectedLesson);
                  const minutesLate = current?.minutesLate || 5;
                  const lateExcused = current?.lateExcused || false;
                  return {
                    ...prev,
                    [student.id]: {
                      status: "late",
                      arrivalTime,
                      minutesLate,
                      lateExcused,
                    },
                  };
                } else if (current?.status === "late") {
                  const next = { ...prev } as typeof prev;
                  delete next[student.id];
                  return next;
                } else {
                  return {
                    ...prev,
                    [student.id]: { status: "present" },
                  };
                }
              })
            }
          >
            {attendance?.status === "late" ? (
              <Clock className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
        </div>
        {/* Inf — Informiert / absent_excused, separate button */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Informiert"
            className={`h-8 w-8 p-0 ${
              attendance?.status === "excused"
                ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                : "text-gray-300 hover:text-blue-600 hover:bg-blue-50"
            }`}
            onClick={() => {
              if (attendance?.status === "excused") {
                // Re-tap clears the mark.
                setTempAttendance((prev) => {
                  const next = { ...prev } as typeof prev;
                  delete next[student.id];
                  return next;
                });
                return;
              }
              // Route through setStudentStatus so the course-absent guard
              // catches Inf transitions on a course lesson with a parallel
              // main class.
              setStudentStatus(student.id, "excused");
              if (courseAbsentGuardActive) return;
              if (isKb) {
                setTempAttendance((prev) => ({
                  ...prev,
                  [student.id]: {
                    ...prev[student.id],
                    status: "excused",
                    excuseReason: "Kurzbeschulung",
                  },
                }));
              }
            }}
          >
            Inf
          </Button>
        </div>
        {/* U — Ungeklärt / absent_unclear, separate button */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Ungeklärt"
            disabled={uDisabled}
            title={
              hasCoveringAbsenceNote
                ? "Eine Abwesenheitsnotiz deckt diesen Tag bereits ab — „Ungeklärt“ ist nicht möglich."
                : undefined
            }
            className={`h-8 w-8 p-0 ${
              attendance?.status === "unexcused"
                ? "text-red-600 bg-red-50 hover:bg-red-100"
                : uDisabled
                ? "text-gray-200 cursor-not-allowed"
                : "text-gray-300 hover:text-red-600 hover:bg-red-50"
            }`}
            onClick={() => {
              if (attendance?.status === "unexcused") {
                setTempAttendance((prev) => {
                  const next = { ...prev } as typeof prev;
                  delete next[student.id];
                  return next;
                });
                return;
              }
              setStudentStatus(student.id, "unexcused");
            }}
          >
            U
          </Button>
        </div>
      </div>
      {attendance?.status === "excused" && (
        <ExcuseField
          studentId={student.id}
          excuseReason={attendance.excuseReason || ""}
          setTempAttendance={setTempAttendance}
          wrapperClass="ml-1 mr-1"
        />
      )}
      {attendance?.status === "late" && (
        <LateFields
          studentId={student.id}
          attendance={attendance}
          selectedLesson={selectedLesson}
          setTempAttendance={setTempAttendance}
          wrapperClass="ml-1 mr-1"
        />
      )}
    </div>
  );
}

// ============================================================================
// Desktop Row
// ============================================================================

function DesktopStudentRow({
  student,
  attendance,
  isKb,
  isCourse,
  nameClass,
  selectedLesson,
  setStudentStatus,
  setTempAttendance,
  formatStudentName,
  onRemoveGuest,
  hasCoveringAbsenceNote,
}: {
  student: EditStudentRowProps["student"];
  attendance: EditStudentRowProps["attendance"];
  isKb: boolean;
  isCourse: boolean;
  nameClass: string;
  selectedLesson: any;
  setStudentStatus: EditStudentRowProps["setStudentStatus"];
  setTempAttendance: EditStudentRowProps["setTempAttendance"];
  formatStudentName: (name: string) => string;
  onRemoveGuest?: () => void;
  hasCoveringAbsenceNote: boolean;
}) {
  // "U" is blocked while an excusing absence note already covers the
  // day — the absence is explained, so marking it ungeklärt would
  // spawn a bogus Klärung.
  const uDisabled = isKb || hasCoveringAbsenceNote;
  return (
    <div
      className="grid gap-2 items-center py-2 px-2 hover:bg-gray-50 rounded"
      style={{ gridTemplateColumns: "3fr repeat(4, 1fr)" }}
    >
      <StudentName
        student={student}
        isKb={isKb}
        isCourse={isCourse}
        nameClass={nameClass}
        formatStudentName={formatStudentName}
        onRemoveGuest={onRemoveGuest}
      />
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${
            attendance?.status === "present"
              ? "text-green-600 bg-green-50 hover:bg-green-100"
              : "text-gray-300 hover:text-green-600 hover:bg-green-50"
          }`}
          onClick={() => setStudentStatus(student.id, "present")}
        >
          <Check className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${
            attendance?.status === "late"
              ? "text-yellow-600 bg-yellow-50 hover:bg-yellow-100"
              : "text-gray-300 hover:text-yellow-600 hover:bg-yellow-50"
          }`}
          onClick={() => setStudentStatus(student.id, "late")}
        >
          <Clock className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${
            attendance?.status === "excused"
              ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
              : "text-gray-300 hover:text-blue-600 hover:bg-blue-50"
          }`}
          onClick={() => setStudentStatus(student.id, "excused")}
          aria-label="Informiert"
          title="Informiert"
        >
          Inf
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          disabled={uDisabled}
          title={
            hasCoveringAbsenceNote
              ? "Eine Abwesenheitsnotiz deckt diesen Tag bereits ab — „Ungeklärt“ ist nicht möglich."
              : undefined
          }
          className={`h-8 w-8 p-0 ${
            attendance?.status === "unexcused"
              ? "text-red-600 bg-red-50 hover:bg-red-100"
              : uDisabled
              ? "text-gray-200 cursor-not-allowed"
              : "text-gray-300 hover:text-red-600 hover:bg-red-50"
          }`}
          onClick={() => setStudentStatus(student.id, "unexcused")}
        >
          U
        </Button>
      </div>
      {attendance?.status === "excused" && (
        <ExcuseField
          studentId={student.id}
          excuseReason={attendance.excuseReason || ""}
          setTempAttendance={setTempAttendance}
          wrapperClass="col-span-5 ml-4 mr-2"
        />
      )}
      {attendance?.status === "late" && (
        <LateFields
          studentId={student.id}
          attendance={attendance}
          selectedLesson={selectedLesson}
          setTempAttendance={setTempAttendance}
          wrapperClass="col-span-5 ml-4 mr-2"
        />
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function EditStudentRow({
  student,
  attendance,
  isMobile,
  isKb,
  isCourse,
  isPending,
  pendingRed,
  selectedLesson,
  setStudentStatus,
  setTempAttendance,
  formatStudentName,
  onRemoveGuest,
  hasCoveringAbsenceNote = false,
}: EditStudentRowProps) {
  const nameClass = isPending && pendingRed ? "text-red-600 font-medium" : "";

  return (
    <div className="space-y-2">
      {isMobile ? (
        <MobileStudentRow
          student={student}
          attendance={attendance}
          isKb={isKb}
          isCourse={isCourse}
          nameClass={nameClass}
          selectedLesson={selectedLesson}
          setStudentStatus={setStudentStatus}
          setTempAttendance={setTempAttendance}
          formatStudentName={formatStudentName}
          onRemoveGuest={onRemoveGuest}
          hasCoveringAbsenceNote={hasCoveringAbsenceNote}
        />
      ) : (
        <DesktopStudentRow
          student={student}
          attendance={attendance}
          isKb={isKb}
          isCourse={isCourse}
          nameClass={nameClass}
          selectedLesson={selectedLesson}
          setStudentStatus={setStudentStatus}
          setTempAttendance={setTempAttendance}
          formatStudentName={formatStudentName}
          onRemoveGuest={onRemoveGuest}
          hasCoveringAbsenceNote={hasCoveringAbsenceNote}
        />
      )}
    </div>
  );
}
